import { ComparisonPatch } from '@sketch-hq/sketch-web-renderer'
import { CanvasSelection } from '@sketch/canvas'

import {
  Change,
  EntityChange,
  EntityChangeKind,
  LayerChange,
  Quad,
} from './layerTypes'

/**
 * Whether the canvas has no node for this change's entity. Changes are named
 * from the newer version, so which kind is missing depends on the side shown.
 */
export function isAbsentFromCanvas(
  kind: EntityChangeKind,
  isViewingNewer: boolean
): boolean {
  return isViewingNewer ? kind === 'removed' : kind === 'added'
}

/**
 * Where the other version of a changed thing belongs: its quad from the side
 * that is not on screen. A change of either kind carries one for each side.
 */
export function otherVersionQuad(
  change: LayerChange | EntityChange | null,
  isViewingNewer: boolean
): Quad | null {
  if (!change) return null

  return isViewingNewer ? change.quadBefore : change.quadAfter
}

/** The four, in a fixed order, so nothing here walks the object's own keys. */
export const cornersOf = ({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
}: Quad) => [topLeft, topRight, bottomRight, bottomLeft]

/**
 * Half a document pixel. The two quads are walked by different matrix chains -
 * one in the canvas, one in the comparison - so a layer that never moved still
 * comes back a few millionths apart, and exact equality would outline it twice.
 */
const COINCIDE_WITHIN = 0.5

/**
 * Whether the quad is a place at all. The comparison puts all four corners on
 * the origin to say the two versions do not have it anywhere different, and
 * nothing with no area can be drawn around in any case.
 */
const hasArea = (quad: Quad) => {
  const corners = cornersOf(quad)
  const xs = corners.map(({ x }) => x)
  const ys = corners.map(({ y }) => y)

  return Math.max(...xs) > Math.min(...xs) && Math.max(...ys) > Math.min(...ys)
}

const quadsCoincide = (one: Quad, other: Quad) => {
  const against = cornersOf(other)

  return cornersOf(one).every(
    (corner, index) =>
      Math.abs(corner.x - against[index].x) < COINCIDE_WITHIN &&
      Math.abs(corner.y - against[index].y) < COINCIDE_WITHIN
  )
}

/** Whether something at `from` is now at `to`, a real place that is not `from`. */
export const hasMoved = (from: Quad, to: Quad) =>
  hasArea(to) && !quadsCoincide(from, to)

/**
 * Where a frame or Section's other version is, when that is not where this one
 * is. Only one both versions have: an added or removed one is already marked
 * where it is.
 */
export function movedEntityQuad(
  change: LayerChange | EntityChange | null,
  isViewingNewer: boolean
): Quad | null {
  if (change?.kind !== 'modified') return null

  const [here, there] = isViewingNewer
    ? [change.quadAfter, change.quadBefore]
    : [change.quadBefore, change.quadAfter]

  return hasMoved(here, there) ? there : null
}

/** What a line laid across an entity can show of the entity's own node. */
const isVisual = (property: string) =>
  property.startsWith('style.') ||
  property === 'shape' ||
  property === 'images' ||
  property === 'corners' ||
  property === 'text' ||
  property === 'hidden'

/**
 * The same for a layer inside it, which has a wider answer: a layer that moved
 * within a frame that did not is shown in two places by the line, where the
 * frame moving would only shift both halves together.
 */
const movesWithinTheBox = (property: string) =>
  isVisual(property) ||
  property.startsWith('position.') ||
  property.startsWith('size.') ||
  property === 'rotation'

/** Whether drawing the other version in place would look any different. */
const drawsDifferently = (changes: readonly Change[]) =>
  changes.some(({ property }) => movesWithinTheBox(property))

/**
 * Whether any layer inside the entity changed in a way that is drawn.
 * `layerChangeCount` counts every changed layer, a renamed one included, so it
 * cannot answer this on its own.
 */
const hasVisibleLayerChange = (
  entityUUID: string,
  layers: readonly LayerChange[]
) =>
  layers.some(
    layer =>
      layer.entityUUID === entityUUID &&
      (layer.kind !== 'modified' || drawsDifferently(layer.changes))
  )

/**
 * Whether a changed entity can be shown split: both versions have it at the
 * same size, so one line across it means the same thing on either side, and
 * they differ in a way the line reveals.
 *
 * A resized or merely moved entity gets the swap instead - there is no honest
 * place for a line across two different boxes, and none worth drawing across
 * two identical ones.
 */
export function canSplitEntity(
  change: EntityChange,
  layers: readonly LayerChange[] = []
): boolean {
  if (change.kind !== 'modified') return false

  const resized = change.changes.some(
    ({ property }) => property === 'size.width' || property === 'size.height'
  )

  if (resized) return false

  if (change.changes.some(({ property }) => isVisual(property))) return true

  return hasVisibleLayerChange(change.uuid, layers)
}

/**
 * Whether a changed layer can be swapped: both versions have it, and they draw
 * it differently. A layer only renamed or locked would be swapped for the same
 * pixels, and a button that changes nothing looks broken.
 */
export const canSwapLayer = (change: LayerChange) =>
  change.kind === 'modified' && drawsDifferently(change.changes)

/**
 * The same for a frame or Section, which also draws differently when a layer
 * inside it does. Unlike the split, one that only moved still counts: the swap
 * draws it where the other version had it.
 */
export const canSwapEntity = (
  change: EntityChange,
  layers: readonly LayerChange[] = []
) =>
  change.kind === 'modified' &&
  (drawsDifferently(change.changes) ||
    hasVisibleLayerChange(change.uuid, layers))

/**
 * The one mode a reader has asked for. One at a time: two at once would
 * leave nobody able to say what is on screen. It names no target because it is
 * always for whatever is selected, and moving the selection puts it away.
 * `none` is the reader looking at the version on its own.
 */
export type CompareMode = 'none' | 'split' | 'swap' | 'overlay'

/**
 * What each mode asks the canvas to draw of the other version: in place of the
 * node, or over it as a ghost. The split draws no patch at all - it takes both
 * versions from the one the canvas already holds.
 */
const PATCH_KIND: Partial<Record<CompareMode, ComparisonPatch['kind']>> = {
  swap: 'replace',
  overlay: 'overlay',
}

/**
 * What the canvas is asked to draw from the other version, or null. Only ever
 * one, which is all the renderer takes. Whether the selection can be drawn
 * that way at all is `useCompareMode`'s call, which puts the mode away if not.
 */
export function modePatch(
  selection: CanvasSelection | null,
  change: LayerChange | EntityChange | null,
  mode: CompareMode
): ComparisonPatch | null {
  const kind = PATCH_KIND[mode]

  if (!kind || !selection || change?.kind !== 'modified') return null

  // A uuid repeats for every instance of a symbol, so a layer is named by its
  // path hash as well: the uuid says what it is, the hash which instance
  if (selection.kind === 'layer') {
    return selection.layerPathHash
      ? {
          kind,
          id: selection.uuid,
          layerPath: selection.layerPathHash,
        }
      : null
  }

  return { kind, id: selection.uuid }
}
